import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useApiClient, useAuth } from "./AuthProvider";

export type TenantOption = {
  id: string;
  name: string;
};

type TenantContextType = {
  tenants: TenantOption[];
  currentTenantId: string | null;
  setCurrentTenantId: (id: string) => void;
  hasMultipleTenants: boolean;
  loading: boolean;
};

const TenantContext = createContext<TenantContextType | null>(null);

const STORAGE_KEY = "current_tenant_id";

type GroupRow = {
  id: string;
  name: string;
  label: "tenant" | "state" | "site" | "tag";
};

export const TenantProvider = ({ children }: { children: React.ReactNode }) => {
  const apiClient = useApiClient();
  const { user, isAuthenticated, permissions } = useAuth();

  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [currentTenantId, setCurrentTenantIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const setCurrentTenantId = useCallback((id: string) => {
    setCurrentTenantIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setTenants([]);
      setCurrentTenantIdState(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiClient.get<{ groups: GroupRow[] }>("/api/groups");
        if (cancelled) return;

        const tenantNames = new Map(data.groups.filter((g) => g.label === "tenant").map((g) => [g.id, g.name]));
        const tenantIds = new Set([
          ...data.groups.filter((g) => g.label === "tenant").map((g) => g.id),
          ...(permissions?.accessibleTenantIds ?? []),
        ]);

        const accessibleTenants = [...tenantIds]
          .map((id) => ({ id, name: tenantNames.get(id) ?? id }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setTenants(accessibleTenants);

        // Pick default: stored selection (if still accessible) → user's home tenant → first
        const stored = localStorage.getItem(STORAGE_KEY);
        const storedValid = stored && accessibleTenants.some((t) => t.id === stored);
        const homeValid = user?.tenantId && accessibleTenants.some((t) => t.id === user.tenantId);

        const next = storedValid ? stored : homeValid ? user!.tenantId : (accessibleTenants[0]?.id ?? null);

        setCurrentTenantIdState(next);
        if (next) localStorage.setItem(STORAGE_KEY, next);
      } catch (err) {
        console.error("Failed to load tenants:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, apiClient, user?.tenantId, permissions?.accessibleTenantIds]);

  return (
    <TenantContext.Provider
      value={{
        tenants,
        currentTenantId,
        setCurrentTenantId,
        hasMultipleTenants: tenants.length > 1,
        loading,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within a TenantProvider");
  return ctx;
};
